/**
 * What a row says about how old somebody's password is.
 *
 * A plain function because the SPA has no component tests and every branch here is a distinction
 * that matters to somebody reading a list of people: an age nobody knows is not an age of zero, and
 * a password that is merely old is not one that has stopped working.
 */

export type PasswordAgeTone = 'muted' | 'warn' | 'expired'

export interface PasswordAgeLabel {
  text: string
  tone: PasswordAgeTone
}

const DAY_MS = 24 * 60 * 60 * 1000

/** Whole days since the password was set, or null when that is not known. */
export function daysOld(changedUtc: string | null | undefined, now: number): number | null {
  if (!changedUtc) return null
  const changed = Date.parse(changedUtc)
  if (Number.isNaN(changed)) return null
  return Math.max(0, Math.floor((now - changed) / DAY_MS))
}

/**
 * The sentence beside an account, and how loudly to say it.
 *
 * Four states rather than two, and the first is the one a blank cell would hide: an account that
 * existed before ageing did has no timestamp and never expires, which is a fact worth reading rather
 * than a gap to interpret. The console says "Never signed in" for the same reason.
 *
 * With no policy set - which is what nearly every organization has - the age is still reported and
 * is never dressed up as a warning. Saying "expires in 6 days" to an organization that has not asked
 * for expiry would be inventing a deadline.
 */
export function passwordAgeLabel(
  changedUtc: string | null | undefined,
  maxAgeDays: number,
  warnDays: number,
  now: number
): PasswordAgeLabel {
  const age = daysOld(changedUtc, now)
  if (age === null) return { text: 'Password age unknown', tone: 'muted' }

  const plainly = { text: `Password changed ${age} ${age === 1 ? 'day' : 'days'} ago`, tone: 'muted' as const }
  if (maxAgeDays <= 0) return plainly

  const remaining = maxAgeDays - age
  // Already stopped working. A stronger statement than a warning, and never shown beside one.
  if (remaining <= 0) return { text: 'Password expired', tone: 'expired' }

  if (warnDays > 0 && remaining <= warnDays)
    return { text: `Password expires in ${remaining} ${remaining === 1 ? 'day' : 'days'}`, tone: 'warn' }

  return plainly
}
